import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, AlertTriangle, RefreshCcw, Clock, CheckCircle2, XCircle, ShieldCheck, Wrench } from "lucide-react";

interface HealthData {
  generatedAt: string;
  flags: Array<{ severity: "red" | "amber"; label: string; detail: string }>;
  tasks: { today: number; yesterday: number; last7Days: number; daily: Array<{ day: string; count: number }> };
  extractor: { kavyaRepliesToday: number; kavyaRepliesYesterday: number; tasksFromExtractorToday: number; tasksFromExtractorYesterday: number; conversionRatePctToday: number | null };
  viren: { digestsToday: number; prepsToday: number; escalationsToday: number; openAdminEscalations: number; severityDistribution7d: Record<string, number>; bankerOverreactionRatePct14d: number };
  artifacts: { uploadsToday: number };
  notifications: { bankerToday: number; investorToday: number; bankerByKind7d: Record<string, number> };
  trust: {
    windowDays: number;
    closedTasks: number;
    reopenedTasks: number;
    reopenRatePct: number;
    avgReopensPerCompany: number;
    meeraTasksTotal: number;
    meeraTasksDismissed: number;
    meeraPrecisionPct: number | null;
    medianClosureToReopenHours: number | null;
    dismissalReasons: Record<string, number>;
    reopenReasons: Record<string, number>;
  };
  schedulers: Array<{ key: string; label: string; lastRunAt: string | null; lastStatus: string; lastDetail: string | null; hoursSinceLastRun: number | null; stale: boolean }>;
}

export default function AdminHealth() {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("admin_token") || "";
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/health`, {
        headers: { "x-admin-token": token },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e: any) {
      setError(e?.message || "Failed to load");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="page-admin-health">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-serif font-bold tracking-tight">Pipeline Health</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Heartbeat across Kavya → Meera → Viren. If something has gone quiet,
            it shows up here before users complain.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCcw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {error && (
        <Card><CardContent className="p-4 text-sm text-red-600">Failed to load: {error}</CardContent></Card>
      )}

      {data && (
        <>
          {data.flags.length > 0 ? (
            <div className="space-y-2">
              {data.flags.map((f, i) => (
                <Card key={i} className={`border-l-4 ${f.severity === "red" ? "border-l-red-500 bg-red-50/30" : "border-l-amber-500 bg-amber-50/30"}`}>
                  <CardContent className="p-3 flex items-start gap-3">
                    <AlertTriangle className={`w-4 h-4 mt-0.5 ${f.severity === "red" ? "text-red-600" : "text-amber-600"}`} />
                    <div className="flex-1">
                      <p className="text-sm font-semibold">{f.label}</p>
                      <p className="text-xs text-muted-foreground">{f.detail}</p>
                    </div>
                    <Badge variant={f.severity === "red" ? "destructive" : "secondary"}>{f.severity}</Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-l-4 border-l-emerald-500 bg-emerald-50/30">
              <CardContent className="p-3 flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <p className="text-sm">All systems nominal. No flags.</p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Metric label="Tasks today" value={data.tasks.today} sub={`yesterday: ${data.tasks.yesterday} · 7d: ${data.tasks.last7Days}`} />
            <Metric label="From Kavya extractor today" value={data.extractor.tasksFromExtractorToday} sub={`${data.extractor.kavyaRepliesToday} replies → ${data.extractor.conversionRatePctToday ?? 0}% extraction rate`} />
            <Metric label="Banker artifact uploads today" value={data.artifacts.uploadsToday} sub="Calls, decks, transcripts" />
          </div>

          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2">Tasks created — last 7 days</p>
              <div className="flex items-end gap-1 h-24">
                {data.tasks.daily.map(d => {
                  const max = Math.max(1, ...data.tasks.daily.map(x => x.count));
                  const h = Math.round((d.count / max) * 100);
                  return (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-primary/80 rounded-t" style={{ height: `${h}%`, minHeight: d.count > 0 ? 4 : 0 }} title={`${d.day}: ${d.count}`} />
                      <span className="text-[9px] font-mono text-muted-foreground">{d.day.slice(5)}</span>
                      <span className="text-[10px] font-bold">{d.count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4 space-y-2">
                <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Viren today</p>
                <Row label="Digests written (to Meera)" value={data.viren.digestsToday} />
                <Row label="Banker call-prep briefs" value={data.viren.prepsToday} />
                <Row label="Admin escalations created" value={data.viren.escalationsToday} />
                <Row label="Open admin escalations" value={data.viren.openAdminEscalations} />
                <Row label="Banker overreaction rate (14d)" value={`${data.viren.bankerOverreactionRatePct14d}%`} />
                <div className="pt-2 mt-2 border-t border-border">
                  <p className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">Severity mix (7d)</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(data.viren.severityDistribution7d).map(([sev, c]) => (
                      <Badge key={sev} variant="outline" className="text-[10px]">{sev}: {c}</Badge>
                    ))}
                    {Object.keys(data.viren.severityDistribution7d).length === 0 && <span className="text-xs text-muted-foreground">No digests yet.</span>}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-2">
                <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Notifications today</p>
                <Row label="Banker notifications" value={data.notifications.bankerToday} />
                <Row label="Investor notifications" value={data.notifications.investorToday} />
                <div className="pt-2 mt-2 border-t border-border">
                  <p className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">Banker mix (7d, dedupe-collapsed)</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(data.notifications.bankerByKind7d).map(([k, c]) => (
                      <Badge key={k} variant="outline" className="text-[10px]">{k}: {c}</Badge>
                    ))}
                    {Object.keys(data.notifications.bankerByKind7d).length === 0 && <span className="text-xs text-muted-foreground">Quiet day.</span>}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {data.trust && (
            <Card className="border-l-4 border-l-primary/60">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  <p className="text-sm font-semibold">Trust metrics — last {data.trust.windowDays} days</p>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  The loop only closes when the investor stops asking. These four numbers tell you whether closures are real or just queue clearance.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <TrustMetric
                    label="Reopen rate"
                    value={`${data.trust.reopenRatePct}%`}
                    sub={`${data.trust.reopenedTasks} of ${data.trust.closedTasks} closures reopened`}
                    tone={data.trust.reopenRatePct >= 25 ? "warn" : data.trust.reopenRatePct >= 10 ? "soft" : "ok"}
                  />
                  <TrustMetric
                    label="Avg reopens / company"
                    value={data.trust.avgReopensPerCompany.toFixed(2)}
                    sub="Concentrated reopens = one portco hard to answer"
                    tone={data.trust.avgReopensPerCompany >= 1 ? "warn" : "ok"}
                  />
                  <TrustMetric
                    label="Meera precision"
                    value={data.trust.meeraPrecisionPct === null ? "—" : `${data.trust.meeraPrecisionPct}%`}
                    sub={`${data.trust.meeraTasksTotal - data.trust.meeraTasksDismissed} kept · ${data.trust.meeraTasksDismissed} dismissed`}
                    tone={data.trust.meeraPrecisionPct !== null && data.trust.meeraPrecisionPct < 70 ? "warn" : "ok"}
                  />
                  <TrustMetric
                    label="Median time to reopen"
                    value={data.trust.medianClosureToReopenHours === null ? "—" : `${data.trust.medianClosureToReopenHours}h`}
                    sub={data.trust.medianClosureToReopenHours !== null && data.trust.medianClosureToReopenHours < 24 ? "Fast reopens = closure note missed the question" : "Time from close to investor reopening"}
                    tone={data.trust.medianClosureToReopenHours !== null && data.trust.medianClosureToReopenHours < 12 ? "warn" : "ok"}
                  />
                </div>
                {(Object.keys(data.trust.reopenReasons).length > 0 || Object.keys(data.trust.dismissalReasons).length > 0) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-border">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-2">Why investors reopened</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(data.trust.reopenReasons).map(([k, c]) => (
                          <Badge key={k} variant="outline" className="text-[10px]">{k.replace(/_/g, " ")}: {c}</Badge>
                        ))}
                        {Object.keys(data.trust.reopenReasons).length === 0 && <span className="text-xs text-muted-foreground">No reopens.</span>}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-2">Why bankers dismissed Meera tasks</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(data.trust.dismissalReasons).map(([k, c]) => (
                          <Badge key={k} variant="outline" className="text-[10px]">{k.replace(/_/g, " ")}: {c}</Badge>
                        ))}
                        {Object.keys(data.trust.dismissalReasons).length === 0 && <span className="text-xs text-muted-foreground">No dismissals.</span>}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <NotificationRepairCard />

          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-3">Schedulers</p>
              <div className="space-y-2">
                {data.schedulers.map(s => (
                  <div key={s.key} className="flex items-start justify-between gap-3 p-3 rounded border bg-card">
                    <div className="flex items-start gap-2">
                      {s.lastStatus === "ok" && !s.stale && <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />}
                      {s.stale && <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />}
                      {s.lastStatus === "error" && <XCircle className="w-4 h-4 text-red-600 mt-0.5" />}
                      {s.lastStatus === "never" && <Clock className="w-4 h-4 text-muted-foreground mt-0.5" />}
                      <div>
                        <p className="text-sm font-medium">{s.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {s.lastRunAt
                            ? `Last run ${new Date(s.lastRunAt).toLocaleString()} (${s.hoursSinceLastRun}h ago)`
                            : "Has not reported since process start."}
                        </p>
                        {s.lastDetail && <p className="text-[10px] text-muted-foreground/70 font-mono mt-0.5">{s.lastDetail}</p>}
                      </div>
                    </div>
                    <Badge variant={s.stale || s.lastStatus === "error" ? "destructive" : s.lastStatus === "ok" ? "secondary" : "outline"}>
                      {s.lastStatus}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <p className="text-[10px] text-muted-foreground font-mono text-right">Generated {new Date(data.generatedAt).toLocaleString()}</p>
        </>
      )}
    </div>
  );
}

function Metric({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground">{label}</p>
        <p className="text-3xl font-bold mt-1">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function TrustMetric({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: "ok" | "soft" | "warn" }) {
  const toneClass = tone === "warn" ? "text-red-600" : tone === "soft" ? "text-amber-600" : "text-foreground";
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${toneClass}`}>{value}</p>
      <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{sub}</p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

interface TruncationStats {
  total: number;
  byCategory: Record<string, { total: number; trailingEllipsis: number; midWord: number; suspect: number }>;
}
interface RepairResult {
  scanned: number;
  rewritten: number;
  skipped: number;
  errors: number;
  byCategory: Record<string, { scanned: number; rewritten: number; skipped: number; errors: number }>;
  sampleRewrites?: Array<{ id: number; category: string; oldLen: number; newLen: number }>;
}

function NotificationRepairCard() {
  const [stats, setStats] = useState<TruncationStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RepairResult | null>(null);
  const [resultError, setResultError] = useState<string | null>(null);

  const headers = () => ({
    "Content-Type": "application/json",
    "x-admin-token": localStorage.getItem("admin_token") || "",
  });

  const loadStats = async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/maintenance/notification-truncation-stats`, { headers: headers() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStats(await r.json());
    } catch (e: any) {
      setStatsError(e?.message || "Failed to load stats");
    }
    setStatsLoading(false);
  };

  useEffect(() => { loadStats(); }, []);

  const runRepair = async () => {
    if (!confirm("Rebuild all truncated notification bodies from their original sources? Safe to re-run; this only rewrites bodies that look chopped.")) return;
    setRunning(true);
    setResultError(null);
    setResult(null);
    try {
      const r = await fetch(`${import.meta.env.BASE_URL}api/admin/maintenance/refresh-truncated-notifications`, {
        method: "POST",
        headers: headers(),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e?.error || `HTTP ${r.status}`);
      }
      setResult(await r.json());
      loadStats();
    } catch (e: any) {
      setResultError(e?.message || "Repair failed");
    }
    setRunning(false);
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-muted-foreground" />
            <p className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Notification Repair</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadStats} disabled={statsLoading}>
            <RefreshCcw className={`w-3.5 h-3.5 mr-2 ${statsLoading ? "animate-spin" : ""}`} /> Recheck
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
          Rewrites old inbox notifications whose body got chopped mid-word, by re-deriving the full text from the original
          chat / task / outreach record. Safe to run multiple times — only repairs ones that still look truncated.
        </p>

        {statsError && <p className="text-xs text-red-600 mb-2">Stats failed: {statsError}</p>}

        {stats && (
          <div className="rounded border bg-muted/40 p-3 mb-3 text-xs">
            <div className="flex items-center justify-between mb-2">
              <span className="text-muted-foreground">Suspected truncated notifications across all categories</span>
              <span className="font-mono font-bold">{stats.total}</span>
            </div>
            <div className="space-y-1">
              {Object.entries(stats.byCategory).map(([cat, c]) => (
                <div key={cat} className="flex items-center justify-between font-mono text-[11px] text-muted-foreground">
                  <span>{cat}</span>
                  <span>
                    {c.suspect} / {c.total}
                    {c.midWord > 0 && <span className="text-amber-600 ml-2">mid-word: {c.midWord}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button onClick={runRepair} disabled={running || !stats || stats.total === 0} size="sm">
          {running ? <><RefreshCcw className="w-3.5 h-3.5 mr-2 animate-spin" /> Repairing</> : <><Wrench className="w-3.5 h-3.5 mr-2" /> Run repair</>}
        </Button>

        {resultError && <p className="text-xs text-red-600 mt-3">Repair failed: {resultError}</p>}

        {result && (
          <div className="mt-3 rounded border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/20 p-3 text-xs">
            <p className="font-semibold text-emerald-700 dark:text-emerald-400 mb-2">
              Done. Rewrote {result.rewritten} notification{result.rewritten === 1 ? "" : "s"}
              {result.skipped > 0 && <span className="text-muted-foreground font-normal"> · skipped {result.skipped}</span>}
              {result.errors > 0 && <span className="text-red-600 font-normal"> · errors {result.errors}</span>}.
            </p>
            <div className="space-y-1 font-mono text-[11px] text-muted-foreground">
              {Object.entries(result.byCategory).map(([cat, c]) => (
                <div key={cat} className="flex items-center justify-between">
                  <span>{cat}</span>
                  <span>scanned {c.scanned} · rewrote {c.rewritten}{c.errors > 0 && <span className="text-red-600"> · err {c.errors}</span>}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
